import { useRef } from "react";
import ImageUploader from "@/components/base/ImageUploader";
import {
  BuilderBlock,
  HeroBlockData,
  StoryBlockData,
  ApplicationsBlockData,
  CertificationsBlockData,
  CTABlockData,
  SpecsGridBlockData,
  GalleryBlockData,
  VideoBlockData,
} from "./types";

interface Props {
  block: BuilderBlock;
  isSelected: boolean;
  onClick: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onInlineChange: (updated: BuilderBlock) => void;
  isFirst: boolean;
  isLast: boolean;
  previewOnly?: boolean;
}

// ── Inline editable text ──────────────────────────────────────────────────────
interface InlineTextProps {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
  tag?: "h1" | "h2" | "h3" | "h4" | "p" | "span";
  multiline?: boolean;
}

const InlineText: React.FC<InlineTextProps> = ({
  value, onChange, className = "", placeholder = "Nhấp để chỉnh sửa...", tag = "p", multiline = false
}) => {
  const ref = useRef<HTMLElement>(null);

  const handleBlur = () => {
    if (ref.current) {
      onChange(ref.current.innerText);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
  };

  const Tag = tag as keyof JSX.IntrinsicElements;

  return (
    <Tag
      ref={ref as React.RefObject<HTMLElement & HTMLHeadingElement & HTMLParagraphElement & HTMLSpanElement>}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
      className={`outline-none cursor-text hover:ring-2 hover:ring-[#004A99]/30 hover:ring-offset-1 rounded transition-all ${className} ${!value ? "text-gray-300" : ""}`}
      data-placeholder={placeholder}
      style={{ minWidth: 40 }}
    >
      {value || ""}
    </Tag>
  );
};

// ── Inline image replacer ─────────────────────────────────────────────────────
interface InlineImageProps {
  src: string;
  onChange: (url: string) => void;
  className?: string;
  folder?: string;
  placeholder?: string;
}

const InlineImage: React.FC<InlineImageProps> = ({ src, onChange, className = "", folder = "products", placeholder = "Nhấp để thêm ảnh" }) => {
  const [showUploader, setShowUploader] = React.useState(false);

  return (
    <div className={`relative group/img ${className}`} onClick={(e) => e.stopPropagation()}>
      {src ? (
        <>
          <img src={src} alt="" className="w-full h-full object-cover object-top" />
          {/* Overlay on hover */}
          <div
            className="absolute inset-0 bg-black/0 group-hover/img:bg-black/40 transition-all flex items-center justify-center cursor-pointer"
            onClick={() => setShowUploader(true)}
          >
            <div className="opacity-0 group-hover/img:opacity-100 transition-opacity flex flex-col items-center gap-1 text-white">
              <i className="ri-image-edit-line text-2xl" />
              <span className="text-xs font-semibold">Thay ảnh</span>
            </div>
          </div>
        </>
      ) : (
        <div
          className="w-full h-full flex flex-col items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#004A99] hover:bg-[#EDF2FB] transition-colors"
          onClick={() => setShowUploader(true)}
        >
          <i className="ri-image-add-line text-3xl text-gray-400" />
          <span className="text-xs text-gray-400 mt-1">{placeholder}</span>
        </div>
      )}

      {showUploader && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowUploader(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-gray-800">Chọn hoặc tải ảnh lên</p>
              <button type="button" onClick={() => setShowUploader(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <ImageUploader
              images={src ? [src] : []}
              onChange={(urls) => {
                onChange(urls[0] ?? "");
                setShowUploader(false);
              }}
              maxImages={1}
              folder={folder}
              label="Tải ảnh lên"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Need React import for useState in InlineImage
import React from "react";

// ── Hero Preview (Inline Editable) ────────────────────────────────────────────
const HeroPreview: React.FC<{ data: HeroBlockData; blockId: string; onInlineChange: (updated: BuilderBlock) => void }> = ({ data, blockId, onInlineChange }) => {
  const update = (patch: Partial<HeroBlockData>) => {
    onInlineChange({ id: blockId, data: { ...data, ...patch } });
  };

  return (
    <div className="relative w-full rounded-xl overflow-hidden" style={{ minHeight: 220 }}>
      {/* Background image — inline replaceable */}
      <InlineImage
        src={data.backgroundImage}
        onChange={(url) => update({ backgroundImage: url })}
        className="absolute inset-0 w-full h-full"
        folder="products"
        placeholder="Nhấp để thêm ảnh nền hero"
      />

      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{ background: `rgba(0,0,0,${data.overlayOpacity / 100})` }}
      />

      {/* Content — all inline editable */}
      <div
        className={`relative z-10 flex flex-col gap-2 px-8 py-10 ${
          data.textAlign === "center" ? "items-center text-center" :
          data.textAlign === "right" ? "items-end text-right" : "items-start text-left"
        }`}
      >
        {/* Tagline */}
        <InlineText
          tag="span"
          value={data.tagline}
          onChange={(v) => update({ tagline: v })}
          className="text-xs font-bold tracking-widest text-white/70 uppercase"
          placeholder="TAGLINE"
        />

        {/* Title */}
        <InlineText
          tag="h2"
          value={data.title}
          onChange={(v) => update({ title: v })}
          className="text-2xl font-bold text-white leading-tight"
          placeholder="Tiêu đề sản phẩm"
        />

        {/* Subtitle */}
        <InlineText
          tag="p"
          value={data.subtitle}
          onChange={(v) => update({ subtitle: v })}
          className="text-sm text-white/80 max-w-lg"
          placeholder="Mô tả ngắn..."
          multiline
        />

        {/* CTA Button */}
        <InlineText
          tag="span"
          value={data.ctaLabel}
          onChange={(v) => update({ ctaLabel: v })}
          className="mt-2 px-5 py-2 bg-white text-[#004A99] text-sm font-bold rounded-full inline-block"
          placeholder="Nút CTA"
        />
      </div>

      {/* Overlay opacity control — shown on hover */}
      <div className="absolute bottom-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="bg-black/60 rounded-lg px-3 py-2 flex items-center gap-2">
          <i className="ri-contrast-2-line text-white text-xs" />
          <input
            type="range" min={0} max={90} step={5}
            value={data.overlayOpacity}
            onChange={(e) => update({ overlayOpacity: Number(e.target.value) })}
            onClick={(e) => e.stopPropagation()}
            className="w-20 accent-white"
          />
          <span className="text-white text-xs">{data.overlayOpacity}%</span>
        </div>
      </div>
    </div>
  );
};

// ── Story Preview ─────────────────────────────────────────────────────────────
const StoryPreview: React.FC<{ data: StoryBlockData; blockId: string; onInlineChange: (updated: BuilderBlock) => void }> = ({ data, blockId, onInlineChange }) => {
  const updateItem = (idx: number, patch: Partial<typeof data.items[0]>) => {
    const items = data.items.map((item, i) => i === idx ? { ...item, ...patch } : item);
    onInlineChange({ id: blockId, data: { ...data, items } });
  };

  return (
    <div className="space-y-4">
      {data.items.length === 0 && (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
          <i className="ri-layout-column-line text-2xl block mb-1" />
          Mở panel chỉnh sửa để thêm story blocks
        </div>
      )}
      {data.items.map((item, i) => (
        <div
          key={item.id}
          className={`flex gap-4 items-center ${item.imagePosition === "right" ? "flex-row-reverse" : ""}`}
        >
          <InlineImage
            src={item.image}
            onChange={(url) => updateItem(i, { image: url })}
            className="w-32 h-24 rounded-xl overflow-hidden flex-shrink-0"
            folder="products"
            placeholder="Thêm ảnh"
          />
          <div className="flex-1 min-w-0">
            {item.accent && (
              <InlineText
                tag="span"
                value={item.accent}
                onChange={(v) => updateItem(i, { accent: v })}
                className="text-[10px] font-bold tracking-wider text-[#004A99] uppercase bg-[#EDF2FB] px-2 py-0.5 rounded-full inline-block"
                placeholder="ACCENT"
              />
            )}
            <InlineText
              tag="h4"
              value={item.title}
              onChange={(v) => updateItem(i, { title: v })}
              className="font-bold text-gray-800 mt-1 text-sm"
              placeholder={`Story ${i + 1}`}
            />
            <div
              className="text-xs text-gray-500 mt-1 line-clamp-2"
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Applications Preview ──────────────────────────────────────────────────────
const ApplicationsPreview: React.FC<{ data: ApplicationsBlockData; blockId: string; onInlineChange: (updated: BuilderBlock) => void }> = ({ data, blockId, onInlineChange }) => {
  const updateItem = (idx: number, patch: Partial<typeof data.items[0]>) => {
    const items = data.items.map((item, i) => i === idx ? { ...item, ...patch } : item);
    onInlineChange({ id: blockId, data: { ...data, items } });
  };

  return (
    <div>
      <div className="mb-3">
        <InlineText
          tag="h3"
          value={data.title}
          onChange={(v) => onInlineChange({ id: blockId, data: { ...data, title: v } })}
          className="font-bold text-gray-800 text-sm"
          placeholder="Tiêu đề ứng dụng"
        />
        <InlineText
          tag="p"
          value={data.subtitle}
          onChange={(v) => onInlineChange({ id: blockId, data: { ...data, subtitle: v } })}
          className="text-xs text-gray-500 mt-0.5"
          placeholder="Mô tả phụ..."
        />
      </div>
      {data.items.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
          <i className="ri-apps-line text-2xl block mb-1" />
          Mở panel chỉnh sửa để thêm ứng dụng
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {data.items.slice(0, 6).map((item, i) => (
            <div key={item.id} className="rounded-xl overflow-hidden border border-gray-100">
              <InlineImage
                src={item.image_url}
                onChange={(url) => updateItem(i, { image_url: url })}
                className="h-20 w-full"
                folder="applications"
                placeholder="Thêm ảnh"
              />
              <div className="p-2">
                <InlineText
                  tag="p"
                  value={item.title}
                  onChange={(v) => updateItem(i, { title: v })}
                  className="text-xs font-semibold text-gray-700 truncate"
                  placeholder={`Ứng dụng ${i + 1}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Certifications Preview ────────────────────────────────────────────────────
const CertificationsPreview: React.FC<{ data: CertificationsBlockData; blockId: string; onInlineChange: (updated: BuilderBlock) => void }> = ({ data, blockId, onInlineChange }) => {
  const updateItem = (idx: number, patch: Partial<typeof data.items[0]>) => {
    const items = data.items.map((item, i) => i === idx ? { ...item, ...patch } : item);
    onInlineChange({ id: blockId, data: { ...data, items } });
  };

  return (
    <div>
      <InlineText
        tag="h3"
        value={data.title}
        onChange={(v) => onInlineChange({ id: blockId, data: { ...data, title: v } })}
        className="font-bold text-gray-800 text-sm mb-3"
        placeholder="Tiêu đề chứng nhận"
      />
      {data.items.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
          <i className="ri-award-line text-2xl block mb-1" />
          Mở panel chỉnh sửa để thêm chứng nhận
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {data.items.map((item, i) => (
            <div key={item.id} className="border border-gray-100 rounded-xl p-3 text-center">
              <InlineImage
                src={item.image_url}
                onChange={(url) => updateItem(i, { image_url: url })}
                className="w-12 h-12 mx-auto mb-2 rounded-xl overflow-hidden"
                folder="patents"
                placeholder="Ảnh"
              />
              <InlineText
                tag="p"
                value={item.title}
                onChange={(v) => updateItem(i, { title: v })}
                className="text-xs font-semibold text-gray-700 truncate"
                placeholder="Tên chứng nhận"
              />
              <span className="text-[10px] text-gray-400">{item.type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── CTA Preview ───────────────────────────────────────────────────────────────
const CTAPreview: React.FC<{ data: CTABlockData; blockId: string; onInlineChange: (updated: BuilderBlock) => void }> = ({ data, blockId, onInlineChange }) => (
  <div
    className="rounded-xl p-6 text-center"
    style={{ background: data.backgroundColor, color: data.textColor }}
  >
    <InlineText
      tag="h3"
      value={data.title}
      onChange={(v) => onInlineChange({ id: blockId, data: { ...data, title: v } })}
      className="font-bold text-lg"
      placeholder="Tiêu đề CTA"
    />
    <InlineText
      tag="p"
      value={data.subtitle}
      onChange={(v) => onInlineChange({ id: blockId, data: { ...data, subtitle: v } })}
      className="text-sm opacity-80 mt-1"
      placeholder="Mô tả phụ..."
    />
    {data.showForm && (
      <div className="mt-4 grid grid-cols-2 gap-2 max-w-sm mx-auto">
        {data.formFields.slice(0, 4).map((f, i) => (
          <div
            key={i}
            className={`bg-white/20 rounded-lg px-3 py-2 text-xs text-left ${f.type === "textarea" ? "col-span-2" : ""}`}
          >
            <span className="opacity-70">{f.label}{f.required ? " *" : ""}</span>
          </div>
        ))}
      </div>
    )}
    <InlineText
      tag="span"
      value={data.buttonLabel}
      onChange={(v) => onInlineChange({ id: blockId, data: { ...data, buttonLabel: v } })}
      className="mt-4 px-6 py-2 rounded-full text-sm font-bold inline-block cursor-text"
      placeholder="Nút CTA"
    />
  </div>
);

// ── Specs Grid Preview ────────────────────────────────────────────────────────
const SpecsGridPreview: React.FC<{ data: SpecsGridBlockData; blockId: string; onInlineChange: (updated: BuilderBlock) => void }> = ({ data, blockId, onInlineChange }) => {
  const updateItem = (idx: number, field: "label" | "value", val: string) => {
    const items = data.items.map((item, i) => i === idx ? { ...item, [field]: val } : item);
    onInlineChange({ id: blockId, data: { ...data, items } });
  };

  return (
    <div>
      <InlineText
        tag="h3"
        value={data.title}
        onChange={(v) => onInlineChange({ id: blockId, data: { ...data, title: v } })}
        className="font-bold text-gray-800 text-sm mb-3"
        placeholder="Tiêu đề thông số"
      />
      {data.items.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
          <i className="ri-table-line text-2xl block mb-1" />
          Mở panel chỉnh sửa để thêm thông số
        </div>
      ) : (
        <div className={`grid gap-2 ${data.columns === 2 ? "grid-cols-2" : data.columns === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
          {data.items.slice(0, 9).map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
              <InlineText
                tag="p"
                value={item.label}
                onChange={(v) => updateItem(i, "label", v)}
                className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider"
                placeholder="Tên thông số"
              />
              <InlineText
                tag="p"
                value={item.value}
                onChange={(v) => updateItem(i, "value", v)}
                className="text-sm font-bold text-gray-800 mt-0.5"
                placeholder="Giá trị"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Gallery Preview ───────────────────────────────────────────────────────────
const GalleryPreview: React.FC<{ data: GalleryBlockData; blockId: string; onInlineChange: (updated: BuilderBlock) => void }> = ({ data, blockId, onInlineChange }) => {
  const updateImage = (idx: number, url: string) => {
    const images = data.images.map((img, i) => i === idx ? { ...img, url } : img);
    onInlineChange({ id: blockId, data: { ...data, images } });
  };

  return (
    <div>
      <InlineText
        tag="h3"
        value={data.title}
        onChange={(v) => onInlineChange({ id: blockId, data: { ...data, title: v } })}
        className="font-bold text-gray-800 text-sm mb-3"
        placeholder="Tiêu đề gallery"
      />
      {data.images.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
          <i className="ri-gallery-line text-2xl block mb-1" />
          Mở panel chỉnh sửa để thêm hình ảnh
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {data.images.slice(0, 6).map((img, i) => (
            <InlineImage
              key={i}
              src={img.url}
              onChange={(url) => updateImage(i, url)}
              className="aspect-square rounded-xl overflow-hidden"
              folder="gallery"
              placeholder="Thêm ảnh"
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Video Preview ─────────────────────────────────────────────────────────────
const VideoPreview: React.FC<{ data: VideoBlockData; blockId: string; onInlineChange: (updated: BuilderBlock) => void }> = ({ data, blockId, onInlineChange }) => {
  const update = (patch: Partial<VideoBlockData>) => {
    onInlineChange({ id: blockId, data: { ...data, ...patch } });
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl overflow-hidden border border-gray-200 bg-black">
        {data.url ? (
          <video
            src={data.url}
            poster={data.poster || undefined}
            className="w-full max-h-[300px] object-contain"
            controls={data.controls}
            autoPlay={data.autoplay}
            loop={data.loop}
            muted={data.muted}
            playsInline
            preload="metadata"
            style={{ display: "block" }}
          />
        ) : (
          <div className="w-full h-[180px] flex flex-col items-center justify-center bg-gray-100">
            <i className="ri-video-add-line text-3xl text-gray-400" />
            <p className="text-xs text-gray-400 mt-1">Chưa có video</p>
          </div>
        )}
      </div>
      {data.caption && (
        <InlineText
          tag="p"
          value={data.caption}
          onChange={(v) => update({ caption: v })}
          className="text-xs text-gray-500 text-center italic"
          placeholder="Chú thích video..."
        />
      )}
    </div>
  );
};

// ── Main BlockPreview ─────────────────────────────────────────────────────────
const BlockPreview: React.FC<Props> = ({
  block,
  isSelected,
  onClick,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onInlineChange,
  isFirst,
  isLast,
  previewOnly = false,
}) => {
  const typeLabels: Record<string, string> = {
    hero: "Hero Banner",
    story: "Product Story",
    applications: "Ứng dụng",
    certifications: "Chứng nhận",
    cta: "CTA / Liên hệ",
    specs_grid: "Thông số kỹ thuật",
    gallery: "Thư viện ảnh",
    video: "Video",
  };

  const typeIcons: Record<string, string> = {
    hero: "ri-image-2-line",
    story: "ri-layout-column-line",
    applications: "ri-apps-line",
    certifications: "ri-award-line",
    cta: "ri-mail-send-line",
    specs_grid: "ri-table-line",
    gallery: "ri-gallery-line",
    video: "ri-video-line",
  };

  const renderContent = () => {
    switch (block.data.type) {
      case "hero": return <HeroPreview data={block.data as HeroBlockData} blockId={block.id} onInlineChange={onInlineChange} />;
      case "story": return <StoryPreview data={block.data as StoryBlockData} blockId={block.id} onInlineChange={onInlineChange} />;
      case "applications": return <ApplicationsPreview data={block.data as ApplicationsBlockData} blockId={block.id} onInlineChange={onInlineChange} />;
      case "certifications": return <CertificationsPreview data={block.data as CertificationsBlockData} blockId={block.id} onInlineChange={onInlineChange} />;
      case "cta": return <CTAPreview data={block.data as CTABlockData} blockId={block.id} onInlineChange={onInlineChange} />;
      case "specs_grid": return <SpecsGridPreview data={block.data as SpecsGridBlockData} blockId={block.id} onInlineChange={onInlineChange} />;
      case "gallery": return <GalleryPreview data={block.data as GalleryBlockData} blockId={block.id} onInlineChange={onInlineChange} />;
      case "video": return <VideoPreview data={block.data as VideoBlockData} blockId={block.id} onInlineChange={onInlineChange} />;
      default: return null;
    }
  };

  return (
    <div
      className={`group relative rounded-2xl border-2 transition-all ${
        isSelected && !previewOnly
          ? "border-[#004A99] ring-2 ring-[#004A99]/20"
          : "border-gray-200 hover:border-[#004A99]/40"
      } ${previewOnly ? "border-transparent hover:border-transparent shadow-none ring-0" : "cursor-pointer"}`}
      onClick={previewOnly ? undefined : onClick}
    >
      {!previewOnly && (
        /* Block label bar */
        <div
          className={`flex items-center justify-between px-4 py-2 rounded-t-xl border-b ${
            isSelected ? "bg-[#004A99] border-[#004A99]" : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <i
              className={`${typeIcons[block.data.type] ?? "ri-layout-line"} text-sm ${
                isSelected ? "text-white" : "text-[#004A99]"
              }`}
            />
            <span className={`text-xs font-bold ${isSelected ? "text-white" : "text-gray-700"}`}>
              {typeLabels[block.data.type] ?? block.data.type}
            </span>
            {isSelected && (
              <span className="text-[10px] text-white/60 ml-1">
                · Nhấp vào text/ảnh để chỉnh sửa trực tiếp
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={onMoveUp}
              disabled={isFirst}
              title="Di chuyển lên"
              className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer disabled:opacity-30 ${
                isSelected ? "hover:bg-white/20 text-white" : "hover:bg-gray-200 text-gray-500"
              }`}
            >
              <i className="ri-arrow-up-s-line text-sm" />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={isLast}
              title="Di chuyển xuống"
              className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer disabled:opacity-30 ${
                isSelected ? "hover:bg-white/20 text-white" : "hover:bg-gray-200 text-gray-500"
              }`}
            >
              <i className="ri-arrow-down-s-line text-sm" />
            </button>
            <button
              type="button"
              onClick={onDuplicate}
              title="Nhân đôi"
              className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                isSelected ? "hover:bg-white/20 text-white" : "hover:bg-gray-200 text-gray-500"
              }`}
            >
              <i className="ri-file-copy-line text-sm" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              title="Xóa block"
              className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                isSelected ? "hover:bg-red-400/40 text-red-200" : "hover:bg-red-50 text-red-400"
              }`}
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </div>
      )}

      {/* Block content preview */}
      <div className={previewOnly ? "" : "p-4"}>{renderContent()}</div>
    </div>
  );
};

export default BlockPreview;
